import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '@mui/material';
import { Button, Typography, Box, Chip, Switch, FormControlLabel, Alert, Skeleton } from '@mui/material';
import { Check, X } from '@mui/icons-material';
import { useTranslate } from '@refinedev/core';
import CheckoutButton from '../../components/pricing/CheckoutButton';
import { useAuth } from '../../contexts/auth-context';
import { useSubscription } from '../../hooks/useSubscription';
import { useStripePrices } from '../../hooks/useStripePrices';

interface PricingPlan {
  name: string;
  features: string[];
  monthlyPrice?: number;
  annualPrice?: number;
  caseMonthlyPrice?: number;
  caseAnnualPrice?: number;
  limitations?: string[];
  stripeKey: string;
}

const PricingPage: React.FC = () => {
  const translate = useTranslate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, isLoading: authLoading } = useAuth();
  const {
    subscriptionInfo,
    isLoading: isLoadingSubscription,
    error: subscriptionError
  } = useSubscription();
  const { prices, loading: pricesLoading, error: pricesError } = useStripePrices();

  // Memoized plans to prevent unnecessary re-renders
  const plans = useMemo(() => {
    const proPlan: PricingPlan = {
      name: translate('pricing.plans.pro.name'),
      stripeKey: 'pro',
      features: [
        translate('pricing.plans.pro.features.teamMembers'),
        translate('pricing.plans.pro.features.analytics'),
        translate('pricing.plans.pro.features.prioritySupport'),
        translate('pricing.plans.pro.features.enhancedSecurity'),
        translate('pricing.plans.pro.features.apiAccess'),
        translate('pricing.plans.pro.features.storage'),
        translate('pricing.plans.pro.features.integrations'),
        translate('pricing.plans.pro.features.workflows'),
        translate('pricing.plans.pro.features.collaboration'),
        translate('pricing.plans.pro.features.reporting'),
      ],
      limitations: [
        translate('pricing.plans.pro.limitations.noWhiteLabel'),
        translate('pricing.plans.pro.limitations.noAccountManager'),
      ],
    };

    const proCasePlan: PricingPlan = {
      name: translate('pricing.plans.proPlus.name'),
      stripeKey: 'pro_case',
      features: [
        translate('pricing.plans.proPlus.features.everything'),
        translate('pricing.plans.proPlus.features.alarm'),
        translate('pricing.plans.proPlus.features.activation'),
        translate('pricing.plans.proPlus.features.battery'),
        translate('pricing.plans.proPlus.features.magsafe'),
        translate('pricing.plans.proPlus.features.silent'),
        translate('pricing.plans.proPlus.features.waterproof'),
        translate('pricing.plans.proPlus.features.compact'),
        translate('pricing.plans.proPlus.features.led'),
        translate('pricing.plans.proPlus.features.reflex'),
        translate('pricing.plans.proPlus.features.secure'),
      ],
    };

    const eventPlan: PricingPlan = {
      name: translate('pricing.plans.event.name'),
      stripeKey: 'event',
      features: [
        translate('pricing.plans.event.features.unlimited'),
        translate('pricing.plans.event.features.analytics'),
        translate('pricing.plans.event.features.prioritySupport'),
        translate('pricing.plans.event.features.enhancedSecurity'),
        translate('pricing.plans.event.features.fullApi'),
        translate('pricing.plans.event.features.unlimitedStorage'),
        translate('pricing.plans.event.features.customIntegrations'),
        translate('pricing.plans.event.features.customWorkflows'),
        translate('pricing.plans.event.features.collaboration'),
        translate('pricing.plans.event.features.reporting'),
        translate('pricing.plans.event.features.whiteLabel'),
        translate('pricing.plans.event.features.sla'),
        translate('pricing.plans.event.features.training'),
        translate('pricing.plans.event.features.onSite'),
        translate('pricing.plans.event.features.accountManager'),
      ],
    };

    return [proPlan, proCasePlan, eventPlan];
  }, [translate]);

  // Calculate prices from Stripe data using shared utility
  const getPlanPrices = (plan: PricingPlan) => {
    console.log(`🔍 Calculating prices for plan: ${plan.name} (${plan.stripeKey})`);
    console.log(`📊 Available prices:`, prices ? Object.keys(prices) : 'No prices loaded');

    if (!prices || plan.stripeKey === 'event') {
      console.log(`❌ No prices available for ${plan.name}`);
      return { monthlyPrice: undefined, annualPrice: undefined };
    }

    // Map plan keys to actual Stripe price IDs
    const priceIdMap: { [key: string]: { monthly: string; annual: string } } = {
      'pro': {
        monthly: 'price_1RtQUqH3YB1pNNHqROWuGHwg',
        annual: 'price_1RtQUBH3YB1pNNHqwiPXxX2E'
      },
      'pro_case': {
        monthly: 'price_1RtQuOH3YB1pNNHqyASoltsm',
        annual: 'price_1RtQufH3YB1pNNHqH3jHi4Rq'
      }
    };

    const priceIds = priceIdMap[plan.stripeKey];
    if (!priceIds) {
      console.log(`❌ No price mapping found for ${plan.stripeKey}`);
      return { monthlyPrice: undefined, annualPrice: undefined };
    }

    console.log(`🔑 Looking for price IDs: ${priceIds.monthly}, ${priceIds.annual}`);

    const monthlyPrice = prices[priceIds.monthly]?.unit_amount ? prices[priceIds.monthly].unit_amount / 100 : undefined;
    const annualPrice = prices[priceIds.annual]?.unit_amount ? prices[priceIds.annual].unit_amount / 100 : undefined;

    console.log(`💰 Calculated prices for ${plan.name}:`, {
      monthlyPrice,
      annualPrice,
      monthlyPriceId: priceIds.monthly,
      annualPriceId: priceIds.annual,
      monthlyPriceData: prices[priceIds.monthly],
      annualPriceData: prices[priceIds.annual]
    });

    return { monthlyPrice, annualPrice };
  };

  const handleBillingPortal = async () => {
    if (!user?.email) {
      setError(translate('pricing.errors.userNotConnected'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use cached customer ID if available
      let customerId = subscriptionInfo.customerId;

      if (!customerId) {
        // First, search for customer by email
        const searchResponse = await fetch('http://localhost:3002/search-customer', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: user.email }),
        });

        if (!searchResponse.ok) {
          throw new Error(translate('pricing.errors.customerSearchError'));
        }

        const searchData = await searchResponse.json();

        if (!searchData.customerId) {
          setError(translate('pricing.errors.noSubscriptionFound'));
          return;
        }

        customerId = searchData.customerId;
      }

      // Create portal session
      const portalResponse = await fetch('http://localhost:3002/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          returnUrl: window.location.href,
        }),
      });

      const portalData = await portalResponse.json();

      if (!portalResponse.ok) {
        throw new Error(portalData.details || portalData.error || translate('pricing.errors.billingSessionError'));
      }

      if (!portalData.url) {
        throw new Error(translate('pricing.errors.noPortalUrl'));
      }

      window.location.href = portalData.url;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : translate('pricing.errors.billingPortalError');
      setError(errorMessage);
      console.error('Portal error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show loading skeleton while auth, subscription, or prices are loading
  if (authLoading || isLoadingSubscription || pricesLoading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mx: 'auto', mb: 2 }} />
        <Skeleton variant="text" width="40%" height={30} sx={{ mx: 'auto', mb: 4 }} />
        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
          {[1, 2, 3].map((index) => (
            <Card key={index} sx={{ width: 350 }}>
              <CardHeader>
                <Skeleton variant="text" width="80%" height={40} />
              </CardHeader>
              <CardContent>
                <Skeleton variant="text" width="60%" height={50} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="85%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="75%" height={20} sx={{ mb: 3 }} />
                <Skeleton variant="rectangular" width="100%" height={50} />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          {subscriptionInfo.hasActiveSubscription ? translate('pricing.currentSubscription') : translate('pricing.title')}
        </Typography>
      </Box>

      {/* Enhanced subscription info for active subscribers */}
      {subscriptionInfo.hasActiveSubscription && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Alert severity="info" sx={{ maxWidth: 600 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {translate('pricing.subscriptionDetails')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{translate('pricing.plan')} :</strong> {subscriptionInfo.currentPlan}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{translate('pricing.status')} :</strong> {subscriptionInfo.status}
            </Typography>
            {subscriptionInfo.quantity && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{translate('pricing.userCount')} :</strong> {subscriptionInfo.quantity}
              </Typography>
            )}
            {subscriptionInfo.unitPrice && subscriptionInfo.unitPrice > 0 && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{translate('pricing.pricePerUserDetail')} :</strong> €{(subscriptionInfo.unitPrice / 100).toFixed(2)}/mois
              </Typography>
            )}
            {subscriptionInfo.renewalDate ? (
              <Typography variant="body2">
                <strong>{translate('pricing.renewalDate')} :</strong> {new Date(subscriptionInfo.renewalDate * 1000).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            ) : (
              <Typography variant="body2">
                <strong>{translate('pricing.renewalDate')} :</strong> {translate('pricing.unavailableDate')}
              </Typography>
            )}
          </Alert>
        </Box>
      )}

      {/* Cancellation Message */}
      {subscriptionInfo.hasActiveSubscription && subscriptionInfo.isCancelled && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Alert severity="warning" sx={{ maxWidth: 600 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {translate('pricing.subscriptionCancelled')}
            </Typography>
            <Typography variant="body2">
              {translate('pricing.cancelledMessage', {
                date: subscriptionInfo.endDate
                  ? new Date(subscriptionInfo.endDate * 1000).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : translate('pricing.endOfPeriod')
              })}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Only show toggles if user doesn't have active subscription */}
      {!subscriptionInfo.hasActiveSubscription && (
        <>
          {/* Billing Cycle Toggle for Pro */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isAnnual}
                  onChange={(e) => setIsAnnual(e.target.checked)}
                />
              }
              label={translate('pricing.billingAnnual')}
            />
          </Box>




        </>
      )}

      {/* Plans Display */}
      <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
        {plans.map((plan, index) => {
          const { monthlyPrice, annualPrice } = getPlanPrices(plan);
          const price = isAnnual ? (annualPrice || 0) : (monthlyPrice || 0);
          const priceText = translate('pricing.pricePerUser');

          const isCurrentPlan = subscriptionInfo.hasActiveSubscription && plan.name === subscriptionInfo.currentPlan;
          const isProCasePlan = plan.stripeKey === 'pro_case';

          // Only show the current plan card if user has active subscription
          if (subscriptionInfo.hasActiveSubscription && !isCurrentPlan) {
            return null;
          }

          return (
            <Card
              key={plan.name}
              sx={{
                width: 350,
                position: 'relative',
                ...(index === 0 && !subscriptionInfo.hasActiveSubscription && {
                  border: '2px solid',
                  borderColor: 'primary.main',
                }),
                ...(isCurrentPlan && {
                  border: '2px solid',
                  borderColor: subscriptionInfo.isCancelled ? 'warning.main' : 'success.main',
                  bgcolor: subscriptionInfo.isCancelled ? 'warning.50' : 'success.50',
                  overflow: 'visible',
                }),
              }}
            >
              {index === 0 && !subscriptionInfo.hasActiveSubscription && (
                <Chip
                  label={translate('pricing.popularPlan')}
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 100,
                  }}
                />
              )}

              {isCurrentPlan && (
                <Chip
                  label={subscriptionInfo.isCancelled ? translate('pricing.cancelled') : translate('pricing.currentPlan')}
                  color={subscriptionInfo.isCancelled ? "warning" : "success"}
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 100,
                  }}
                />
              )}

              <CardHeader
                title={plan.name}
                titleTypographyProps={{ variant: 'h5', component: 'h2' }}
                sx={{ textAlign: 'center' }}
              />

              <CardContent>
                {plan.stripeKey !== 'event' ? (
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h4" component="div" color="primary">
                      {subscriptionInfo.hasActiveSubscription && isCurrentPlan ? translate('pricing.active') : `€${price.toFixed(2)}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {subscriptionInfo.hasActiveSubscription && isCurrentPlan ? translate('pricing.subscriptionActive') : priceText}
                    </Typography>
                    {!subscriptionInfo.hasActiveSubscription && (
                      <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1 }}>
                        {translate('pricing.freeTrial')}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h4" component="div" color="primary">
                      {translate('pricing.contactUs')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {translate('pricing.customPricing')}
                    </Typography>
                  </Box>
                )}

                {/* Features */}
                <Typography variant="h6" gutterBottom>
                  {translate('pricing.featuresIncluded')}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {plan.features.map((feature) => (
                    <Box key={feature} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Check color="success" sx={{ mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Limitations */}
                {plan.limitations && plan.limitations.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom>
                      {translate('pricing.notIncluded')}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      {plan.limitations.map((limitation: string) => (
                        <Box key={limitation} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <X color="error" sx={{ mr: 1, fontSize: 20 }} />
                          <Typography variant="body2">{limitation}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}

                {/* CTA Button */}
                {plan.stripeKey === 'event' ? (
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    sx={{ mt: 2 }}
                  >
                    {translate('pricing.contactUs')}
                  </Button>
                ) : subscriptionInfo.hasActiveSubscription && isCurrentPlan ? (
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mt: 2, bgcolor: 'success.main' }}
                    onClick={handleBillingPortal}
                    disabled={loading}
                  >
                    {loading ? translate('pricing.loading') : translate('pricing.manageBilling')}
                  </Button>
                ) : (
                  <CheckoutButton
                    selectedType={plan.stripeKey === 'pro_case' ? 'pro_case' : 'pro'}
                    duration={isAnnual ? 'annual' : 'monthly'}
                    isAnnual={isAnnual}
                    people={1}
                    planName={plan.name}
                    popular={index === 0}
                  />
                )}


              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default PricingPage;
